import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';

import {
  LessonTemplate,
  ShortLessonTemplate,
} from './types';
import { toLessonTemplate, toShortLessonTemplates } from './utils';

export const lessonTemplatesApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getLessonTemplates: build.query<CognitoAPIResponse<ShortLessonTemplate[]>, {
      course?: string,
      lessonGroup?: string
    }>({
      query(arg) {
        const { course, lessonGroup } = arg;
        return {
          url: 'lessons/templates/query',
          method: 'GET',
          params: {
            course,
            lessonGroup,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'LessonTemplates' as const, id })), 'LessonTemplates']
        : ['LessonTemplates']),
      transformResponse(response: any) {
        return toShortLessonTemplates(response);
      },
    }),
    getLessonTemplate: build.query<CognitoAPIResponse<LessonTemplate>, {
      course: string,
      lessonId: string,
    }>({
      query(arg) {
        const { course, lessonId } = arg;
        return {
          url: `lessons/templates/${course}/${lessonId}`,
          method: 'GET',
        };
      },
      providesTags: (result) => [{
        type: 'LessonTemplates',
        id: result?.data.id,
      }],
      transformResponse(response: any) {
        return toLessonTemplate(response);
      },
    }),
    addPermissionToLessonTemplate: build.mutation<any, {
      course: string,
      lessonId: string,
      permissionId: string,
    }>({
      query(arg) {
        const { course, lessonId, permissionId } = arg;

        return {
          url: `lessons/templates/${course}/${lessonId}/permissions/add`,
          body: { permissionId },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'LessonTemplates', id: `${arg.course}#${arg.lessonId}` }],
    }),
    removePermissionFromLessonTemplate: build.mutation<any, {
      course: string,
      lessonId: string,
      permissionId: string,
    }>({
      query(arg) {
        const { course, lessonId, permissionId } = arg;

        return {
          url: `lessons/templates/${course}/${lessonId}/permissions/remove`,
          body: { permissionId },
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'LessonTemplates', id: `${arg.course}#${arg.lessonId}` }],
    }),
    updateLessonTemplate: build.mutation<any, {
      course: string,
      lessonId: string,
      newAttributes: any,
    }>({
      query(arg) {
        const { course, lessonId, newAttributes } = arg;

        return {
          url: `lessons/templates/${course}/${lessonId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'LessonTemplates', id: `${arg.course}#${arg.lessonId}` }],
    }),
    deleteLessonTemplate: build.mutation<any, {
      course: string,
      lessonId: string
    }>({
      query(arg) {
        const { course, lessonId } = arg;

        return {
          url: `lessons/templates/${course}/${lessonId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'LessonTemplates', id: `${arg.course}#${arg.lessonId}` }],
    }),
    newLessonTemplate: build.mutation<any, {
      course: string,
      attributes?: {
        lessonId?: string,
        lessonGroup?: string,
        label?: string,
        lessonType?: string
      }
    }>({
      query(arg) {
        const { course, attributes } = arg;

        return {
          url: 'lessons/templates/new',
          body: {
            course,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['LessonTemplates'],
    }),
    newLessonTemplatesBatch: build.mutation<any, {
      course: string,
      idPrefix: string,
      labelPrefix: string,
      numLessons: number,
      attributes?: {
        lessonType?: string
      }
    }>({
      query(arg) {
        const { course, idPrefix, labelPrefix, numLessons, attributes } = arg;

        return {
          url: 'lessons/templates/batchNew',
          body: {
            course,
            idPrefix,
            labelPrefix,
            numLessons,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['LessonTemplates'],
    }),
  }),
});

export const {
  useGetLessonTemplatesQuery,
  useGetLessonTemplateQuery,
  useAddPermissionToLessonTemplateMutation,
  useRemovePermissionFromLessonTemplateMutation,
  useUpdateLessonTemplateMutation,
  useDeleteLessonTemplateMutation,
  useNewLessonTemplateMutation,
  useNewLessonTemplatesBatchMutation,
} = lessonTemplatesApi;
